import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { Button, Field, Input, Modal, Select } from "../lib/ui.js";
import { dollarsToCents, formatMoney } from "../lib/money.js";
import { formatDateOnly } from "../lib/datetime.js";

/** What the database says when an adjustment is refused, in words an admin can act on (migration 0052). */
export const ADJUSTMENT_ERROR_TEXT: Record<string, string> = {
  adjustment_memo_invalid: "The memo can be at most 80 characters.",
  adjustment_amount_invalid: "Enter an amount between $0.01 and $100,000.00.",
  adjustment_makes_total_negative: "That credit is more than the invoice total.",
  invoice_not_adjustable: "Only a draft or failed invoice can be adjusted. Once an invoice has been sent, queue the adjustment for the next one instead.",
  adjustments_base_only: "Only base fee invoices can be adjusted.",
  too_many_adjustments: "At most 10 adjustments. Remove one first.",
  no_rate_card: "This client has no rate card for that period, so GST can't be worked out.",
  adjustment_not_found: "That adjustment is already gone. Close and reopen this window.",
  invoice_not_found: "That invoice no longer exists.",
  client_not_found: "That client no longer exists.",
  not_authorized: "Only platform admins can do that.",
};

/**
 * The form shared by "adjust this invoice" and "queue for the next invoice": credit or extra charge, an amount in dollars (never
 * typed with a minus), and an optional memo the client reads after the fixed label "Adjustment". `onAdd` returns an error text, or
 * null when it worked (the form then clears).
 */
export function AdjustmentForm({ onAdd, busy }: { onAdd: (amountCents: number, memo: string) => Promise<string | null>; busy: boolean }) {
  const [kind, setKind] = useState<"credit" | "charge">("credit");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cents = dollarsToCents(amount);
    if (cents === null) {
      setError(ADJUSTMENT_ERROR_TEXT.adjustment_amount_invalid!);
      return;
    }
    const err = await onAdd(kind === "credit" ? -cents : cents, memo);
    setError(err);
    if (!err) {
      setAmount("");
      setMemo("");
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10 }}>
        <Field label="Type">
          <Select value={kind} onChange={(e) => setKind(e.target.value as "credit" | "charge")}>
            <option value="credit">Credit (reduces)</option>
            <option value="charge">Extra charge</option>
          </Select>
        </Field>
        <Field label="Amount (ex GST)">
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="25.00" aria-label="Amount in dollars" />
        </Field>
      </div>
      <Field label="Memo (optional, the client sees it after “Adjustment”)">
        <Input value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={80} placeholder="e.g. Goodwill for the 4 Oct outage" />
      </Field>
      {error && (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: "var(--rc-danger)" }}>
          {error}
        </p>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="submit" variant="primary" disabled={busy || !amount.trim()}>
          {busy ? "Saving..." : "Add adjustment"}
        </Button>
      </div>
    </form>
  );
}

export interface AdjustableInvoice {
  id: string;
  number: string;
  period_start: string;
  period_end: string;
  clientName: string;
}

interface LineRow {
  id: number;
  description: string;
  amount_cents: number;
  adjustment: boolean;
}

interface Totals {
  subtotal_cents: number;
  gst_cents: number;
  total_cents: number;
}

/**
 * Adjusts a draft or failed BASE invoice (migration 0052): add a credit or an extra charge as an extra line, take one off again.
 * The base-fee line is never edited. The database works the GST and total out again from the lines.
 */
export function InvoiceAdjust({ invoice, onClose, onChanged }: { invoice: AdjustableInvoice; onClose: () => void; onChanged: () => void }) {
  const [lines, setLines] = useState<LineRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const [{ data: l }, { data: t }] = await Promise.all([
      supabase.from("invoice_lines").select("id, description, amount_cents, adjustment").eq("invoice_id", invoice.id).order("id"),
      supabase.from("invoices").select("subtotal_cents, gst_cents, total_cents").eq("id", invoice.id).maybeSingle(),
    ]);
    setLines((l ?? []) as LineRow[]);
    setTotals((t ?? null) as Totals | null);
  }

  useEffect(() => {
    void load();
  }, [invoice.id]);

  /** Runs one change; returns the error text, or null on success (after refreshing this window and the page behind it). */
  async function run(call: PromiseLike<{ error: { message: string } | null }>): Promise<string | null> {
    setBusy(true);
    setError(null);
    const { error: err } = await call;
    setBusy(false);
    if (err) return ADJUSTMENT_ERROR_TEXT[err.message] ?? err.message;
    await load();
    onChanged();
    return null;
  }

  const adjustments = lines.filter((l) => l.adjustment).length;

  return (
    <Modal title={`Adjust ${invoice.number}`} onClose={onClose} width={600}>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--rc-text-muted)" }}>
        {invoice.clientName} · {formatDateOnly(invoice.period_start)} – {formatDateOnly(invoice.period_end)}
      </p>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginBottom: 8 }}>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
              <td style={{ padding: "8px 6px" }}>
                {l.description}
                {l.adjustment && <span style={{ marginLeft: 8, fontSize: 12, color: "var(--rc-text-faint)" }}>{l.amount_cents < 0 ? "credit" : "extra charge"}</span>}
              </td>
              <td style={{ padding: "8px 6px", textAlign: "right", whiteSpace: "nowrap" }}>{formatMoney(l.amount_cents)}</td>
              <td style={{ padding: "8px 0 8px 6px", textAlign: "right", width: 80 }}>
                {l.adjustment && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void run(supabase.rpc("rpc_admin_remove_invoice_adjustment", { p_line_id: l.id })).then(setError)}
                  >
                    Remove
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>
            <tr>
              <td style={{ padding: "6px 6px 0" }}>Subtotal (ex GST)</td>
              <td style={{ padding: "6px 6px 0", textAlign: "right" }}>{formatMoney(totals.subtotal_cents)}</td>
              <td />
            </tr>
            <tr>
              <td style={{ padding: "2px 6px" }}>GST</td>
              <td style={{ padding: "2px 6px", textAlign: "right" }}>{formatMoney(totals.gst_cents)}</td>
              <td />
            </tr>
            <tr style={{ color: "var(--rc-text)", fontWeight: 700 }}>
              <td style={{ padding: "2px 6px" }}>Total</td>
              <td style={{ padding: "2px 6px", textAlign: "right" }}>{formatMoney(totals.total_cents)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
      {error && (
        <p role="alert" style={{ margin: "0 0 8px", fontSize: 13, color: "var(--rc-danger)" }}>
          {error}
        </p>
      )}

      <div style={{ marginTop: 16 }}>
        <AdjustmentForm
          busy={busy}
          onAdd={(cents, memo) => run(supabase.rpc("rpc_admin_add_invoice_adjustment", { p_invoice_id: invoice.id, p_amount_cents: cents, p_memo: memo }))}
        />
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <span style={{ marginRight: "auto", fontSize: 12, color: "var(--rc-text-faint)" }}>GST is added at the invoice's rate. {adjustments}/10 adjustments used.</span>
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}

interface PendingRow {
  id: string;
  amount_cents: number;
  memo: string | null;
  created_at: string;
}

/**
 * Adjustments queued for a client's NEXT base invoice (migration 0052). They are added when that invoice is generated (the 15th, or
 * "Generate drafts now"), so they are on it before it is emailed. One that would take the total below $0 stays waiting and the
 * billing alert says so.
 */
export function PendingAdjustments({ clients }: { clients: { id: string; name: string }[] }) {
  const [clientId, setClientId] = useState("");
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(id: string) {
    if (!id) {
      setRows([]);
      return;
    }
    const { data } = await supabase
      .from("invoice_pending_adjustments")
      .select("id, amount_cents, memo, created_at")
      .eq("client_id", id)
      .is("applied_at", null)
      .order("created_at");
    setRows((data ?? []) as PendingRow[]);
  }

  useEffect(() => {
    void load(clientId);
  }, [clientId]);

  async function run(call: PromiseLike<{ error: { message: string } | null }>): Promise<string | null> {
    setBusy(true);
    setError(null);
    const { error: err } = await call;
    setBusy(false);
    if (err) return ADJUSTMENT_ERROR_TEXT[err.message] ?? err.message;
    await load(clientId);
    return null;
  }

  return (
    <fieldset style={{ border: "1px solid var(--rc-border)", borderRadius: 6, padding: 12, marginBottom: 20 }}>
      <legend style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>Adjustments for a client's next base invoice</legend>
      <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "0 0 10px" }}>
        Added automatically when that client's next base invoice is generated (the 15th, or Generate drafts now), before it is emailed. For an invoice that
        already exists as a draft, use Adjust on its row instead.
      </p>
      <label style={{ display: "block", fontSize: 14, marginBottom: 10 }}>
        Client
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          <option value="">Choose a client</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {clientId && (
        <>
          {rows.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--rc-text-faint)", margin: "0 0 10px" }}>Nothing waiting for this client.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginBottom: 12 }}>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                    <td style={{ padding: "6px" }}>
                      Adjustment{r.memo ? ` - ${r.memo}` : ""}
                      <span style={{ marginLeft: 8, fontSize: 12, color: "var(--rc-text-faint)" }}>{r.amount_cents < 0 ? "credit" : "extra charge"}</span>
                    </td>
                    <td style={{ padding: "6px", textAlign: "right", whiteSpace: "nowrap" }}>{formatMoney(r.amount_cents)}</td>
                    <td style={{ padding: "6px 0 6px 6px", textAlign: "right", width: 80 }}>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void run(supabase.rpc("rpc_admin_remove_pending_adjustment", { p_id: r.id })).then(setError)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error && (
            <p role="alert" style={{ margin: "0 0 8px", fontSize: 13, color: "var(--rc-danger)" }}>
              {error}
            </p>
          )}
          <AdjustmentForm
            busy={busy}
            onAdd={(cents, memo) => run(supabase.rpc("rpc_admin_add_pending_adjustment", { p_client_id: clientId, p_amount_cents: cents, p_memo: memo }))}
          />
        </>
      )}
    </fieldset>
  );
}
